import { useCallback, useEffect, useRef, useState } from "react";

export interface AsyncDataState<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | null;
  retry: () => void;
}

export function useAsyncData<T>(
  loader: () => Promise<T>,
  timeoutMs: number = 10000,
  deps: ReadonlyArray<unknown> = [],
): AsyncDataState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const mountedRef = useRef(true);
  // Keep the latest loader in a ref so a fresh closure on every render does
  // NOT cause the effect to re-run. Callers pass any input that the loader
  // closes over in `deps` instead, so changing inputs still triggers a reload
  // but changing identity alone does not.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const retry = useCallback(() => {
    if (!mountedRef.current) return;
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      setLoading(true);
      setError(null);
      timeoutHandle = setTimeout(() => {
        if (!cancelled) {
          setError(new Error("timeout"));
          setLoading(false);
        }
      }, timeoutMs);
      try {
        const value = await loaderRef.current();
        if (!cancelled) {
          setData(value);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (timeoutHandle) clearTimeout(timeoutHandle);
    };
  }, [tick, timeoutMs, ...deps]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { data, loading, error, retry };
}
